import { useEffect, useLayoutEffect, useRef } from "react";

import { useBrowserNotifications } from "features/browser-notifications/browser-notifications.context";
import { isAppBackgrounded } from "features/browser-notifications/browser-notifications.util";

export interface IncomingNotificationCall {
  id: string;
  direction: string;
  state: string;
  from: string;
  fromName: string | null;
  on: (event: "answered" | "ended", handler: () => void) => void;
  off: (event: "answered" | "ended", handler: () => void) => void;
}

const NO_CALLER_NAMES: Record<string, string> = {};

export function useIncomingCallNotifications<
  T extends IncomingNotificationCall
>(
  calls: T[],
  openDialer: () => void,
  answerCall: (call: T) => void,
  // Who Suno knows each call as (matched patient, contact), by call id. Wins
  // over the carrier's caller ID.
  callerNames: Record<string, string> = NO_CALLER_NAMES
) {
  const { notify } = useBrowserNotifications();
  const tracked = useRef(
    new Map<T, { cleanup: () => void; rename: () => void }>()
  );
  const latest = useRef({ calls, openDialer, answerCall, callerNames });
  // Notifications outlive a render. In particular, use the current SDK
  // action so answering call waiting holds the current active call.
  useLayoutEffect(() => {
    latest.current = { calls, openDialer, answerCall, callerNames };
  }, [calls, openDialer, answerCall, callerNames]);

  useEffect(() => {
    const current = tracked.current;
    for (const [call, { cleanup }] of current) {
      if (!calls.includes(call) || call.state === "ended") {
        cleanup();
        current.delete(call);
      }
    }
    for (const call of calls) {
      if (
        call.direction !== "inbound" ||
        call.state !== "ringing" ||
        current.has(call)
      )
        continue;
      const closers: Array<() => void> = [];
      let shownCaller: string | undefined;
      let answerRequested = false;
      const callerOf = () =>
        latest.current.callerNames[call.id] ||
        call.fromName ||
        call.from ||
        "Unknown caller";
      const show = () => {
        const caller = callerOf();
        // The patient match lands a moment after the ring. The renamed banner
        // replaces the first one in place (same collapse key) and stays
        // silent; its own id gets it past the delivered ledger.
        const renamed = shownCaller !== undefined;
        shownCaller = caller;
        closers.push(
          notify({
            type: "incomingCalls",
            id: renamed ? `${call.id}:${closers.length}` : call.id,
            collapseKey: call.id,
            silent: renamed || undefined,
            title: `Incoming call — ${caller}`,
            body: `${caller !== call.from && call.from ? `${call.from}\n` : ""}Click to answer.`,
            requireInteraction: true,
            onClick: () => {
              latest.current.openDialer();
              // A click can race another device answering, call teardown, or
              // another click before the SDK has transitioned out of ringing.
              if (
                answerRequested ||
                call.state !== "ringing" ||
                !current.has(call) ||
                !latest.current.calls.includes(call)
              )
                return;
              answerRequested = true;
              latest.current.answerCall(call);
            }
          })
        );
      };
      const alert = () => {
        if (
          call.state !== "ringing" ||
          !isAppBackgrounded() ||
          shownCaller !== undefined
        )
          return;
        show();
      };
      const rename = () => {
        if (
          call.state !== "ringing" ||
          shownCaller === undefined ||
          shownCaller === callerOf()
        )
          return;
        show();
      };
      const finish = () => {
        closers.forEach((close) => close());
        window.removeEventListener("blur", alert);
        document.removeEventListener("visibilitychange", alert);
      };
      // Listen on the SDK call itself: its state can mutate in place. Close
      // immediately on answer/reject/end rather than waiting for a render.
      call.on("answered", finish);
      call.on("ended", finish);
      window.addEventListener("blur", alert);
      document.addEventListener("visibilitychange", alert);
      current.set(call, {
        cleanup: () => {
          finish();
          call.off("answered", finish);
          call.off("ended", finish);
        },
        rename
      });
      alert();
    }
  }, [calls, notify]);

  useEffect(() => {
    tracked.current.forEach(({ rename }) => rename());
  }, [callerNames]);

  useEffect(() => {
    const current = tracked.current;
    return () => {
      current.forEach(({ cleanup }) => cleanup());
      current.clear();
    };
  }, []);
}
